import React from 'react';
import { FiAlertTriangle, FiShield, FiCheckCircle, FiXCircle, FiFileText } from 'react-icons/fi';

const QNavEngagementManagement: React.FC = () => {
  // Mock data for risk items
  const riskItems = [
    {
      id: 'risk-1',
      title: 'Insufficient Training Resources',
      description: 'Development teams lack adequate training on Q Developer features and best practices.',
      severity: 'high',
      status: 'open',
      assignee: 'John Smith',
      created: '2025-03-15',
    },
    {
      id: 'risk-2',
      title: 'Low Adoption Rate in Backend Team',
      description: 'Backend development team showing resistance to adopting Q Developer tools.',
      severity: 'medium',
      status: 'mitigating',
      assignee: 'Sarah Johnson',
      created: '2025-03-20',
    },
    {
      id: 'risk-3',
      title: 'Integration with Legacy Systems',
      description: 'Challenges integrating Q Developer with existing legacy development workflows.',
      severity: 'critical',
      status: 'open',
      assignee: 'Unassigned',
      created: '2025-04-01',
    },
    {
      id: 'risk-4',
      title: 'Governance Policy Gaps',
      description: 'Current governance policies do not adequately address AI-assisted development.',
      severity: 'medium',
      status: 'mitigated',
      assignee: 'Michael Brown',
      created: '2025-03-10',
    },
    {
      id: 'risk-5',
      title: 'Champion Network Underutilized',
      description: 'Champion network not effectively promoting Q Developer adoption across teams.',
      severity: 'high',
      status: 'open',
      assignee: 'Lisa Chen',
      created: '2025-03-25',
    },
  ];

  // Mock data for weekly adoption trend
  const weeklyAdoption = [
    { week: 'Week 1', users: 124, target: 100 },
    { week: 'Week 2', users: 256, target: 250 },
    { week: 'Week 3', users: 412, target: 400 },
    { week: 'Week 4', users: 568, target: 550 },
    { week: 'Week 5', users: 724, target: 700 },
    { week: 'Week 6', users: 876, target: 850 },
  ];

  // Get severity badge
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <span className="px-2 py-1 bg-error-600 text-white text-xs font-medium rounded-full">Critical</span>;
      case 'high':
        return <span className="px-2 py-1 bg-error-500 text-white text-xs font-medium rounded-full">High</span>;
      case 'medium':
        return <span className="px-2 py-1 bg-warning-500 text-white text-xs font-medium rounded-full">Medium</span>;
      case 'low':
        return <span className="px-2 py-1 bg-info-500 text-white text-xs font-medium rounded-full">Low</span>;
      default:
        return <span className="px-2 py-1 bg-secondary-500 text-white text-xs font-medium rounded-full">Unknown</span>;
    }
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="px-2 py-1 bg-error-100 text-error-700 text-xs font-medium rounded-full">Open</span>;
      case 'mitigating':
        return (
          <span className="px-2 py-1 bg-warning-100 text-warning-700 text-xs font-medium rounded-full">Mitigating</span>
        );
      case 'mitigated':
        return (
          <span className="px-2 py-1 bg-success-100 text-success-700 text-xs font-medium rounded-full">Mitigated</span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
            Unknown
          </span>
        );
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <FiAlertTriangle className="text-error-500" />;
      case 'mitigating':
        return <FiShield className="text-warning-500" />;
      case 'mitigated':
        return <FiCheckCircle className="text-success-500" />;
      default:
        return <FiXCircle className="text-secondary-500" />;
    }
  };

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card">
        <h1 className="text-2xl font-display font-bold mb-2">Engagement Management</h1>
        <p className="text-secondary-200">
          Proactively identify, assess, and mitigate risks in your Q Developer adoption journey
        </p>
      </div>

      {/* 6-Week Adoption Program Chart */}
      <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-secondary-800">6-Week Adoption Program</h2>
          <div className="flex items-center text-sm">
            <div className="flex items-center mr-4">
              <div className="w-3 h-3 bg-primary-600 mr-1"></div>
              <span className="text-secondary-600">Actual Users</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-warning-500 mr-1"></div>
              <span className="text-secondary-600">Target</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          {/* Chart visualization */}
          <div className="h-64 relative bg-secondary-50 rounded-md p-4">
            <div className="absolute inset-0 flex items-end px-4 pb-8">
              <div className="w-full flex items-end space-x-2">
                {weeklyAdoption.map((week, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div className="text-xs text-secondary-500 mb-1">{week.users}</div>
                    <div
                      className="w-full bg-primary-600 rounded-t-md transition-all hover:bg-primary-700 relative"
                      style={{ height: `${week.users * 0.07}px` }}
                    >
                      {/* Target line */}
                      <div
                        className="absolute w-full h-0.5 bg-warning-500"
                        style={{ bottom: `${(week.users - week.target) * 0.07}px` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* X-axis labels */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4">
              {weeklyAdoption.map((week, index) => (
                <div key={index} className="flex-1 text-center">
                  <div className="text-xs font-medium text-secondary-700">{week.week}</div>
                </div>
              ))}
            </div>

            {/* Y-axis grid lines */}
            <div className="absolute inset-0 flex flex-col justify-between px-4 pt-4 pb-8 pointer-events-none">
              <div className="w-full h-px bg-secondary-200"></div>
              <div className="w-full h-px bg-secondary-200"></div>
              <div className="w-full h-px bg-secondary-200"></div>
              <div className="w-full h-px bg-secondary-200"></div>
              <div className="w-full h-px bg-secondary-200"></div>
            </div>
          </div>

          {/* Metrics and progress */}
          <div className="flex flex-col justify-between">
            <div className="bg-secondary-50 rounded-md p-4 mb-4">
              <h3 className="font-medium text-secondary-800 mb-2">Program Metrics</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-secondary-600">Current Adoption</span>
                    <span className="font-medium text-secondary-800">876 users</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div className="h-2 rounded-full bg-primary-600" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-secondary-600">Target Progress</span>
                    <span className="font-medium text-secondary-800">103%</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div className="h-2 rounded-full bg-success-500" style={{ width: '103%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-secondary-600">Time Elapsed</span>
                    <span className="font-medium text-secondary-800">50%</span>
                  </div>
                  <div className="w-full bg-secondary-200 rounded-full h-2">
                    <div className="h-2 rounded-full bg-warning-500" style={{ width: '50%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-secondary-50 rounded-md p-4">
              <h3 className="font-medium text-secondary-800 mb-2">Weekly Growth</h3>
              <div className="flex items-center">
                <div className="text-3xl font-bold text-primary-600">+12.4%</div>
                <div className="ml-3 text-sm text-secondary-600">
                  Average weekly growth rate, exceeding target by 3.2%
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <div className="flex items-center text-success-600">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    ></path>
                  </svg>
                  <span>On track to exceed final target</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Generate Engagement Artifacts */}
      <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
        <h2 className="text-xl font-semibold text-secondary-800 mb-4">Generate Engagement Artifacts</h2>
        <p className="text-secondary-600 mb-4">Create standardized reports and templates for your engagement reviews</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-surface-blue p-5 rounded-qnav border border-primary-100 flex flex-col">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-primary-100 text-primary-700 mr-3">
                <FiFileText className="h-5 w-5" />
              </div>
              <h3 className="font-medium text-secondary-800">Weekly Business Review</h3>
            </div>
            <p className="text-sm text-secondary-600 mb-4 flex-grow">
              Generate a WBR template with adoption metrics, risks, and action items from the past week.
            </p>
            <button className="qnav-btn-primary w-full">Generate WBR</button>
          </div>

          <div className="bg-surface-blue p-5 rounded-qnav border border-primary-100 flex flex-col">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-primary-100 text-primary-700 mr-3">
                <FiFileText className="h-5 w-5" />
              </div>
              <h3 className="font-medium text-secondary-800">Monthly Business Review</h3>
            </div>
            <p className="text-sm text-secondary-600 mb-4 flex-grow">
              Create an MBR with monthly trends, milestone progress, and key accomplishments.
            </p>
            <button className="qnav-btn-primary w-full">Generate MBR</button>
          </div>

          <div className="bg-surface-blue p-5 rounded-qnav border border-primary-100 flex flex-col">
            <div className="flex items-center mb-3">
              <div className="p-2 rounded-full bg-primary-100 text-primary-700 mr-3">
                <FiFileText className="h-5 w-5" />
              </div>
              <h3 className="font-medium text-secondary-800">Quarterly Business Review</h3>
            </div>
            <p className="text-sm text-secondary-600 mb-4 flex-grow">
              Build a comprehensive QBR with strategic insights, ROI analysis, and future roadmap.
            </p>
            <button className="qnav-btn-primary w-full">Generate QBR</button>
          </div>
        </div>

        <div className="mt-4 p-4 bg-secondary-50 rounded-qnav border border-secondary-200">
          <div className="flex items-center">
            <FiCheckCircle className="text-success-500 mr-2" />
            <p className="text-sm text-secondary-700">
              All generated artifacts will be pre-populated with your current engagement data and metrics
            </p>
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
          <div className="text-secondary-600 font-medium mb-1">Total Risks</div>
          <div className="text-3xl font-bold text-secondary-800">5</div>
        </div>
        <div className="bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
          <div className="text-secondary-600 font-medium mb-1">Critical/High</div>
          <div className="text-3xl font-bold text-error-600">3</div>
        </div>
        <div className="bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
          <div className="text-secondary-600 font-medium mb-1">Open</div>
          <div className="text-3xl font-bold text-warning-600">3</div>
        </div>
        <div className="bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
          <div className="text-secondary-600 font-medium mb-1">Mitigated</div>
          <div className="text-3xl font-bold text-success-600">1</div>
        </div>
      </div>

      {/* Risk Table */}
      <div className="bg-surface rounded-qnav shadow-brand-card border border-secondary-200 overflow-hidden">
        <div className="p-4 border-b border-secondary-200 bg-secondary-50">
          <h2 className="text-xl font-semibold text-secondary-800">Risk Register</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Risk Item</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Severity</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Assignee</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Created</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-secondary-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-200">
              {riskItems.map((risk) => (
                <tr key={risk.id} className="hover:bg-secondary-50">
                  <td className="px-4 py-4">
                    <div className="font-medium text-secondary-800">{risk.title}</div>
                    <div className="text-sm text-secondary-600">{risk.description}</div>
                  </td>
                  <td className="px-4 py-4">{getSeverityBadge(risk.severity)}</td>
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <span className="mr-2">{getStatusIcon(risk.status)}</span>
                      {getStatusBadge(risk.status)}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-secondary-700">{risk.assignee}</td>
                  <td className="px-4 py-4 text-secondary-700">{risk.created}</td>
                  <td className="px-4 py-4">
                    <button className="text-primary-600 hover:text-primary-800 font-medium">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mitigation Recommendations */}
      <div className="bg-brand-blue rounded-qnav shadow-brand-card p-6 text-white">
        <h2 className="text-xl font-semibold mb-4">Mitigation Recommendations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-2">Enhance Training Program</h3>
            <p className="text-secondary-300 text-sm">
              Implement a structured training program with role-based learning paths for Q Developer adoption.
            </p>
            <div className="mt-3">
              <button className="text-sm text-brand-orange hover:underline">View Training Plan</button>
            </div>
          </div>
          <div className="bg-secondary-800 p-4 rounded-qnav">
            <h3 className="font-medium mb-2">Strengthen Champion Network</h3>
            <p className="text-secondary-300 text-sm">
              Expand the champion network and provide additional resources and incentives for champions.
            </p>
            <div className="mt-3">
              <button className="text-sm text-brand-orange hover:underline">Champion Toolkit</button>
            </div>
          </div>
        </div>
      </div>

      {/* Risk Tracking */}
      <div className="bg-surface rounded-qnav shadow-brand-card p-6 border border-secondary-200">
        <h2 className="text-xl font-semibold text-secondary-800 mb-4">Risk Tracking & Escalation</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <div>
              <h3 className="font-medium text-secondary-800">Risk Assessment Workshop</h3>
              <p className="text-sm text-secondary-600">Schedule a workshop to identify and assess new risks</p>
            </div>
            <button className="qnav-btn-primary text-sm">Schedule</button>
          </div>

          <div className="flex items-center justify-between p-4 bg-surface-blue rounded-qnav border border-primary-100">
            <div>
              <h3 className="font-medium text-secondary-800">Risk Review Meeting</h3>
              <p className="text-sm text-secondary-600">Weekly review of open risks and mitigation progress</p>
            </div>
            <button className="qnav-btn-primary text-sm">Schedule</button>
          </div>

          <div className="flex items-center justify-between p-4 bg-surface-accent rounded-qnav border border-accent-100">
            <div>
              <h3 className="font-medium text-secondary-800">Escalation Path</h3>
              <p className="text-sm text-secondary-600">Define escalation paths for critical and high risks</p>
            </div>
            <button className="qnav-btn-accent text-sm">Configure</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { QNavEngagementManagement };
